import { RenderCellProps } from '@/components/cashflow/transactions/TableRow';
import useMerchantSelect from '@/components/cashflow/useMerchantSelect';
import useUpdateTransactionEntry from '@/components/cashflow/useUpdateTransactionEntry';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import HighlightedText from '@/components/common/HighlightedText';
import InputField from '@/components/common/InputField';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import { Box, Text } from '@chakra-ui/react';
import { getMerchantName } from '@common/react/cashflow';
import { useEffect, useRef, useState } from 'react';
import MerchantLogo from '../MerchantLogo';

const MerchantCell = ({ transactionEntry, onEdit, query }: RenderCellProps) => {
    const [menuIsOpen, setMenuOpen] = useState<boolean>(false);
    const [isEditing, setEditing] = useState<boolean>(false);
    const updateTransactionEntry = useUpdateTransactionEntry(transactionEntry);

    const { handleLoadOptions, handleCreateOption, merchantValue } =
        useMerchantSelect({
            merchant: transactionEntry.merchant,
        });

    const ref = useRef<HTMLSelectElement>(null);
    const isGoalTransaction = transactionEntry.financialEntity.goal;

    useEffect(() => {
        if (isEditing && ref.current) {
            ref.current.focus();
        }
    }, [isEditing]);

    return (
        <FlexRow m={-2}>
            {isEditing ? (
                <InputField
                    type="fancySelect"
                    name="merchant"
                    ref={ref}
                    w="90%"
                    fancySelectOptions={{
                        fullScreenPlacement: 'right',
                        isClearable: false,
                        menuIsOpen,
                        onMenuOpen: () => setMenuOpen(true),
                        onMenuClose: () => {
                            setMenuOpen(false);
                            onEdit(false);
                            setEditing(false);
                        },
                        defaultOptions: true,
                        loadOptions: handleLoadOptions,
                        onCreateOption: async (name) => {
                            const merchant = await handleCreateOption(name);

                            if (merchant) {
                                await updateTransactionEntry({
                                    variables: {
                                        transactionEntryId: transactionEntry.id,
                                        merchantId: merchant.id,
                                    },
                                });
                            }
                        },
                        placeholder: 'Search for a merchant...',
                        formatCreateLabel: (inputValue) =>
                            `Create merchant "${inputValue}"`,
                    }}
                    value={merchantValue}
                    onChange={async (option: StringOption) => {
                        await updateTransactionEntry({
                            variables: {
                                transactionEntryId: transactionEntry.id,
                                merchantId: option.value,
                            },
                        });
                    }}
                />
            ) : (
                <FlexRow
                    alignItems="center"
                    opacity={transactionEntry.isPending ? 0.5 : 1}
                    py={isGoalTransaction ? 0 : 2}
                    px={2}
                    w="90%"
                    overflow="hidden"
                    borderRadius={6}
                    border="1px solid transparent"
                    h="46px"
                    {...(!isGoalTransaction &&
                        !transactionEntry.isPending &&
                        groupHoverStyleProps({
                            borderColor: 'gray.300',
                        }))}
                    onClick={(e) => {
                        // Prevent updating merchant name for goal transactions and pending transactions
                        if (isGoalTransaction || transactionEntry.isPending) {
                            return;
                        }

                        e.stopPropagation();
                        setEditing(!isEditing);
                        onEdit(!isEditing);
                        setMenuOpen(!isEditing);
                    }}
                >
                    <Box ml={1}>
                        <MerchantLogo merchant={transactionEntry.merchant} />
                    </Box>
                    <FlexCol overflow="hidden">
                        <HighlightedText
                            ml={2}
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                            text={
                                (merchantValue?.label as string | undefined) ||
                                getMerchantName(transactionEntry)
                            }
                            query={query}
                        />
                        {transactionEntry.financialEntity.goal && (
                            <Text
                                ml={2}
                                fontSize="sm"
                                color="gray.500"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                            >
                                {transactionEntry.financialEntity.name}
                            </Text>
                        )}
                    </FlexCol>
                </FlexRow>
            )}
        </FlexRow>
    );
};

export default MerchantCell;
